import PublicLayout from "@/components/PublicLayout";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Cross, ArrowRight } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    anonymous_mode: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await register(form);
    setLoading(false);
    if (res.ok) {
      toast.success("Your private journey has begun.");
      navigate("/dashboard", { replace: true });
    } else {
      setError(res.error);
    }
  };

  return (
    <PublicLayout hideFooter>
      <section className="celestial-bg min-h-[calc(100vh-4rem)] grid place-items-center px-5 lg:px-8 py-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <span className="inline-flex w-12 h-12 rounded-full bg-gradient-to-br from-brand-sky to-brand-sage text-white items-center justify-center mb-4 shadow-sm">
              <Cross className="w-5 h-5" strokeWidth={1.6} />
            </span>
            <h1 className="font-serif text-4xl tracking-tight text-brand-ink">Begin your journey.</h1>
            <p className="text-brand-quiet mt-1">Private. Encrypted. Yours.</p>
          </div>

          <form
            onSubmit={submit}
            className="bg-white rounded-2xl border border-brand-line p-7 space-y-4 soft-shadow"
            data-testid="register-form"
          >
            <div>
              <label className="block text-xs uppercase tracking-wider text-brand-quiet mb-1.5">Name (or a chosen name)</label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="register-name-input"
                className="w-full rounded-xl bg-brand-base border border-brand-line focus:ring-2 focus:ring-brand-sky/40 focus:border-brand-sky outline-none px-4 py-3 text-brand-ink"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-brand-quiet mb-1.5">Email</label>
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="register-email-input"
                className="w-full rounded-xl bg-brand-base border border-brand-line focus:ring-2 focus:ring-brand-sky/40 focus:border-brand-sky outline-none px-4 py-3 text-brand-ink"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-brand-quiet mb-1.5">Password (≥ 6 characters)</label>
              <input
                required
                minLength={6}
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="register-password-input"
                className="w-full rounded-xl bg-brand-base border border-brand-line focus:ring-2 focus:ring-brand-sky/40 focus:border-brand-sky outline-none px-4 py-3 text-brand-ink"
              />
            </div>
            <label className="flex items-start gap-3 text-sm text-brand-quiet cursor-pointer">
              <input
                type="checkbox"
                checked={form.anonymous_mode}
                onChange={(e) => setForm({ ...form, anonymous_mode: e.target.checked })}
                data-testid="register-anonymous-checkbox"
                className="mt-1 accent-brand-sky"
              />
              <span>
                <span className="font-medium text-brand-ink">Use anonymous mode.</span>{" "}
                Your name is hidden everywhere in the app.
              </span>
            </label>

            {error && (
              <p
                data-testid="register-error-message"
                className="text-sm text-[#9a4a3f] bg-[#fbeae6] border border-[#f1cfc6] rounded-lg px-3 py-2"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="register-submit-button"
              className="w-full rounded-full bg-brand-sky text-white hover:bg-brand-skyDark transition-colors px-6 py-3 font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? "Creating…" : (<>Create account <ArrowRight className="w-4 h-4" strokeWidth={1.8} /></>)}
            </button>

            <p className="text-sm text-brand-quiet text-center pt-2">
              Already have an account?{" "}
              <Link to="/login" data-testid="link-to-login" className="text-brand-skyDark hover:underline">
                Sign in
              </Link>
            </p>
          </form>
        </div>
      </section>
    </PublicLayout>
  );
}
